/* eslint class-methods-use-this: 0 */
import { ModuleConsts } from 'client/util/Constants';
import {
  cloneDeep,
  get,
  isEmpty,
  mixin,
  union,
  uniqBy,
} from 'lodash';
import { parallelLimit } from 'async';
import { userStateActionType } from 'client/systemusers/constants/SystemUserConstants';
import BindStatusMixin from 'client/store/mixins/BindStatusMixin';
import CollectionModule from 'client/store/modules/CollectionModule';
import JobApi from 'client/api/v2/JobApi';
import ProgressMixin from 'client/store/mixins/ProgressMixin';
import SudoMixin, {
  addSudoFeaturesToState,
  processSudoBoundItems,
  resetSudoState,
} from 'client/store/mixins/SudoMixin';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import Vue from 'vue';

const { activated, staged, suspended } = ModuleConsts.systemUser.accountStates;

// ----------------------------------------------------------------------------
// Private Helper Methods
// ----------------------------------------------------------------------------

/**
 * Returns an array of the attributes of selected items
 */
const addOtherFeaturesToState = (state) => {
  const state2 = cloneDeep(state);

  // A component can watch this count and take action if it is incremented.
  // For example, a component could refresh a collection if this changes.
  state2.features.modifiedItemCount = 0;

  return state2;
};

const getSelectedItemAttributes = (state, getters) => {
  const selectedItemIds = getters.getSelectedItems;
  return selectedItemIds.reduce((acc, id) => {
    const user = state.entities.boundItems.byId[id];
    if (user) acc.push(user);
    return acc;
  }, []);
};

/**
 * Extend base class to add specific functionality for System Users.
 */
export class SystemUserCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();

    mixin(module.actions, ProgressMixin.actions);

    mixin(module.actions, SudoMixin.actions);
    mixin(module.getters, SudoMixin.getters);
    mixin(module.mutations, SudoMixin.mutations);

    mixin(module.actions, BindStatusMixin.actions);
    mixin(module.getters, BindStatusMixin.getters);
    mixin(module.mutations, BindStatusMixin.mutations);

    module.actions.activateWithEmail = (...params) => this.activateWithEmail(...params);
    module.actions.bulkActivateUsers = (...params) => this.bulkActivateUsers(...params);
    module.actions.bulkForcePasswordChange = (...params) => this.bulkForcePasswordChange(...params);
    module.actions.bulkRequireMFA = (...params) => this.bulkRequireMFA(...params);
    module.actions.bulkResetMFA = (...params) => this.bulkResetMFA(...params);
    module.actions.bulkUnlock = (...params) => this.bulkUnlock(...params);
    module.actions.expire = (...params) => this.expire(...params);
    module.actions.fetchNextScheduledEvent = (...params) => this
      .fetchNextScheduledEvent(...params);
    module.actions.fetchUsersById = (...params) => this.fetchUsersById(...params);
    module.actions.listManagerUsers = (...params) => this.listManagerUsers(...params);
    module.actions.listUsersForManager = (...params) => this.listUsersForManager(...params);
    module.actions.resetMFA = (...params) => this.resetMFA(...params);
    module.actions.scheduleUserStateChange = (...params) => this.scheduleUserStateChange(...params);
    module.actions.selectItems = (...params) => this.selectItems(...params);
    module.actions.syncTotalCount = (...params) => this.syncTotalCount(...params);
    module.actions.unlock = (...params) => this.unlock(...params);
    module.actions.updateModifiedItemCount = (...params) => this.updateModifiedItemCount(...params);
    module.actions.updateNextScheduledEvent = (...params) => this
      .updateNextScheduledEvent(...params);

    module.getters.getInactiveUsers = (...params) => this.getInactiveUsers(...params);
    module.getters.getStagedUsers = (...params) => this.getStagedUsers(...params);
    module.getters.getSuspendedUsers = (...params) => this.getSuspendedUsers(...params);
    module.getters.getUsersIneligibleForForcePasswordChange = (...params) => this
      .getUsersIneligibleForForcePasswordChange(...params);
    module.getters.getUsersIneligibleForMFADisablement = (...params) => this
      .getUsersIneligibleForMFADisablement(...params);
    module.getters.getUsersIneligibleForMFAEnablement = (...params) => this
      .getUsersIneligibleForMFAEnablement(...params);
    module.getters.getUsersIneligibleForMFAReset = (...params) => this
      .getUsersIneligibleForMFAReset(...params);
    module.getters.getUsersIneligibleForActivation = (...params) => this
      .getUsersIneligibleForActivation(...params);
    module.getters.getUsersIneligibleForSuspension = (...params) => this
      .getUsersIneligibleForSuspension(...params);
    module.getters.getUsersIneligibleForUnlock = (...params) => this
      .getUsersIneligibleForUnlock(...params);

    module.mutations.RESET_COLLECTION = (...params) => this.RESET_COLLECTION(...params);
    module.mutations.SET_SCHEDULED_STATE_CHANGES = (...params) => this
      .SET_SCHEDULED_STATE_CHANGES(...params);
    module.mutations.SYNC_TOTAL_COUNT = (...params) => this.SYNC_TOTAL_COUNT(...params);
    module.mutations.UPDATE_MODIFIED_ITEM_COUNT = (...params) => this
      .UPDATE_MODIFIED_ITEM_COUNT(...params);
    module.mutations.UPDATE_SCHEDULED_STATE_CHANGES = (...params) => this
      .UPDATE_SCHEDULED_STATE_CHANGES(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    let state = addSudoFeaturesToState(super.getInitialState());
    state = BindStatusMixin.addBindStatusFeaturesToState(state);
    state = addOtherFeaturesToState(state);
    state.scheduledStateChanges = [];
    return state;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Activate a set of users, with email as appropriate.
   * If the user is suspended, activate normally.
   * If the user is staged, use `activateWithEmail`.
   *
   * @param {Array} stagedUsers - list of users with staged state
   * @param {Array} suspendedUsers - list of users with suspended state
   * @param {Object} settings - staged user settings
   */
  bulkActivateUsers({ dispatch }, { stagedUsers = [], suspendedUsers = [], settings }) {
    return new Promise((resolve) => {
      const tasks = [];
      const response = { numSuccess: 0 };

      suspendedUsers.forEach((user) => {
        tasks.push((cb) => {
          const attributes = {
            state: activated,
            suspended: false, // Will be deprecated in the future
          };
          dispatch('updateItem', { id: user.id, attributes })
            .then((result) => {
              if (result instanceof Error) {
                throw result;
              }
              response.numSuccess += 1;
              dispatch('incrementCompleted');
              cb();
            })
            .catch(() => {
              dispatch('incrementCompleted');
              cb();
            });
        });
      });

      stagedUsers.forEach((user) => {
        tasks.push((cb) => {
          const attributes = {
            alternateEmail: user.alternateEmail,
            email: user.email,
            hasPassword: user.activated,
            settings,
          };
          dispatch('activateWithEmail', { id: user.id, attributes })
            .then((result) => {
              if (result instanceof Error) {
                throw result;
              }
              response.numSuccess += 1;
              dispatch('incrementCompleted');
              cb();
            })
            .catch(() => {
              dispatch('incrementCompleted');
              cb();
            });
        });
      });

      if (tasks.length) {
        dispatch('updateBulkActionStatus', true);
        dispatch('setCounter', { completed: 0, total: tasks.length });
        parallelLimit(tasks, 5, () => {
          dispatch('setCounter', { completed: tasks.length, total: tasks.length });
          dispatch('updateBulkActionStatus', true);
          resolve(response);
        });
      } else {
        resolve();
      }
    });
  }

  /**
   * Expire a set of users to force them to change password at next login.
   * @param {Array} ids - IDs of the users to expire
   */
  bulkForcePasswordChange({ dispatch }, { ids }) {
    return new Promise((resolve) => {
      const response = { numSuccess: 0 };
      const tasks = ids.map(id => (
        (cb) => {
          dispatch('expire', { id })
            .then(() => {
              response.numSuccess += 1;
              dispatch('incrementCompleted');
              cb();
            })
            .catch(() => {
              dispatch('incrementCompleted');
              cb();
            });
        }
      ));

      if (tasks.length) {
        dispatch('updateBulkActionStatus', true);
        dispatch('setCounter', { completed: 0, total: tasks.length });
        parallelLimit(tasks, 5, () => {
          dispatch('setCounter', { completed: tasks.length, total: tasks.length });
          dispatch('updateBulkActionStatus', true);
          resolve(response);
        });
      } else {
        resolve();
      }
    });
  }

  /**
   * Require MFA for a set of users
   * @param {Array} ids - IDs of the users to have MFA reset
   * @param {Number} exclusionUntil - The MFA exclusion expiration date
   * @param {Number} exclusionDays - The number of days for MFA exclusion after user is
   *   moved from staged to activated
   */
  bulkRequireMFA({ dispatch, state }, {
    exclusionUntil, exclusionDays, ids,
  }) {
    /**
     * Keep track of which users to apply enrollment on
     * Already active users should not have enrollment applied
     */
    const selectedUsers = ids.map((id) => {
      const user = state.entities.items.byId[id];
      const mfaConfigured = get(user, 'mfa.configured', false);
      return {
        id,
        includeEnrollment: !mfaConfigured,
        isStagedUser: user.state === staged,
      };
    });

    return new Promise((resolve) => {
      const response = { numSuccess: 0 };
      const tasks = selectedUsers.map(({ id, includeEnrollment, isStagedUser }) => (
        (cb) => {
          const attributes = { enable_user_portal_multifactor: true };

          if (includeEnrollment) {
            if (isStagedUser) {
              attributes.mfa = {
                exclusion: true,
                exclusionDays,
              };
            } else {
              attributes.mfa = {
                exclusion: true,
                exclusionUntil,
              };
            }
          }

          dispatch('updateItem', { id, attributes })
            .then((result) => {
              if (result instanceof Error) {
                throw result;
              }
              response.numSuccess += 1;
              dispatch('incrementCompleted');
              cb();
            })
            .catch(() => {
              dispatch('incrementCompleted');
              cb();
            });
        }
      ));

      if (tasks.length) {
        dispatch('updateBulkActionStatus', true);
        dispatch('setCounter', {
          completed: 0,
          total: tasks.length,
        });
        parallelLimit(tasks, 5, () => {
          dispatch('setCounter', {
            completed: tasks.length,
            total: tasks.length,
          });
          dispatch('updateBulkActionStatus', false);
          resolve(response);
        });
      } else {
        resolve();
      }
    });
  }

  /**
   * Reset MFA for a set of users
   * @param {Array} ids - IDs of the users to have MFA reset
   * @param {Number} exclusionUntil - The MFA exclusion expiration date
   * @param {Number} exclusionDays - The number of days for MFA exclusion after user is
   *   moved from staged to activated
   */
  bulkResetMFA({ dispatch, state }, {
    exclusionUntil, exclusionDays, ids,
  }) {
    return new Promise((resolve) => {
      const { items } = state.entities;
      const response = { numSuccess: 0 };

      const tasks = ids.map(id => (
        (cb) => {
          const isStagedUser = items.byId[id] && items.byId[id].state === staged;

          let payload;
          if (isStagedUser) {
            payload = {
              exclusionDays,
              id,
            };
          } else {
            payload = {
              exclusionUntil,
              id,
            };
          }

          dispatch('resetMFA', payload)
            .then(() => {
              response.numSuccess += 1;
              dispatch('incrementCompleted');
              cb();
            })
            .catch(() => {
              dispatch('incrementCompleted');
              cb();
            });
        }
      ));

      if (tasks.length) {
        dispatch('updateBulkActionStatus', true);
        dispatch('setCounter', {
          completed: 0,
          total: tasks.length,
        });
        parallelLimit(tasks, 5, () => {
          dispatch('setCounter', {
            completed: tasks.length,
            total: tasks.length,
          });
          dispatch('updateBulkActionStatus', false);
          resolve(response);
        });
      } else {
        resolve();
      }
    });
  }

  /**
   * Unlock a set of users
   * @param {Array} ids - IDs of the users to unlock
   */
  bulkUnlock({ dispatch }, { ids }) {
    return new Promise((resolve) => {
      const response = { numSuccess: 0 };
      const tasks = ids.map(id => (
        (cb) => {
          dispatch('unlock', { id })
            .then(() => {
              response.numSuccess += 1;
              dispatch('incrementCompleted');
              cb();
            })
            .catch(() => {
              dispatch('incrementCompleted');
              cb();
            });
        }
      ));

      if (tasks.length) {
        dispatch('updateBulkActionStatus', true);
        dispatch('setCounter', { completed: 0, total: tasks.length });
        parallelLimit(tasks, 5, () => {
          dispatch('setCounter', { completed: tasks.length, total: tasks.length });
          dispatch('updateBulkActionStatus', true);
          resolve(response);
        });
      } else {
        resolve();
      }
    });
  }

  /**
   * Activate a user with email
   * A staged user that has activated set to true => staged with password
   *
   * @param {Object}  settings - Indicates whether to activate with email and what type of email
   *
   * |----------------------|-----------------|---------------|------------------------|
   * | User Type            | Alternate email | Company email | Action                 |
   * |----------------------|-----------------|---------------|------------------------|
   * | staged with password |       X         |               | activate(id, email)    |
   * | staged with password |                 |       X       | activate(id, true)     |
   * | staged with password |                 |               | activate(id, false)    |
   * |----------------------|-----------------|---------------|------------------------|
   * | staged w/o password  |       X         |               | activate(id, true)     |
   * | staged w/o password  |                 |       X       | activate(id, email)    |
   * | staged w/o password  |                 |               | activate(id, false)    |
   * |----------------------|-----------------|---------------|------------------------|
   */
  activateWithEmail(context, { id, attributes }) {
    const sendToAlternateEmail = 1;
    const sendToCompanyEmail = 2;
    const {
      alternateEmail, email, hasPassword, settings,
    } = attributes;
    const {
      alternateEmailOption,
      isAlternateEmailChecked,
      isWelcomeEmailChecked,
      welcomeEmailOption,
    } = settings;

    let payload = false;

    if (hasPassword && isWelcomeEmailChecked) {
      payload = (welcomeEmailOption === sendToCompanyEmail) ? true : alternateEmail;
    } else if (!hasPassword && isAlternateEmailChecked) {
      payload = (alternateEmailOption === sendToAlternateEmail) ? true : email;
    }

    return SystemUserApi.activate(id, payload);
  }

  /**
   * Expire a user
   *
   * @param {String} id - The unique identifier of the user
   */
  expire({ commit }, { id }) {
    return SystemUserApi.expire(id)
      .then(() => {
        commit('UPDATE_ITEM', {
          id,
          attributes: {
            password_expired: true,
          },
        });
      });
  }

  /**
   * Override the fetch so that we can process bound items.
   */
  fetchCollection(context, { next = false } = {}) {
    return super
      .fetchCollection(context, { next })
      .then((ids) => {
        if (ids) {
          BindStatusMixin.processBindStatusItems(context, ids);
          // Fetch the next scheduled jobs for the list of users
          context.dispatch('fetchNextScheduledEvent', ids);
        }

        return ids;
      });
  }

  /**
   * Get the list of scheduled jobs for user state changes at a depth of <DEPTH>
   * for the page of users
   *
   * @todo For now, depth is 1
   */
  fetchNextScheduledEvent({ commit }, ids) {
    return JobApi
      .getNextScheduledEvent(ids)
      .then((changes) => {
        commit('SET_SCHEDULED_STATE_CHANGES', cloneDeep(changes));
      });
  }

  fetchUsersById(_, { ids }) {
    const filterValue = ids.join('|');
    return SystemUserApi.fetch(
      0,
      100,
      'name',
      undefined,
      undefined,
      [{
        customType: 'or', name: '_id', operator: 'in', value: filterValue,
      }],
    );
  }

  /**
   * Gets a list of users that are assigned as managers of other users
   */
  async listManagerUsers({ state }, { ids }) {
    const selectedUsers = await SystemUserApi.managers(ids);
    const managers = selectedUsers.map((id) => {
      const user = state.entities.boundItems.byId[id];
      return {
        id,
        name: `${user.lastname || 'Unknown'}, ${user.firstname || 'Unknown'}`,
        email: user.email,
      };
    });
    // Sort users by Last Name
    const compare = (a, b) => {
      if (a.name > b.name) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      return 0;
    };
    return managers.sort(compare);
  }

  async listUsersForManager(ctx, { searchTerm = '' }) {
    const skip = 0;
    const limit = 100;
    const sort = 'lastname';

    const { results } = await this.Api.fetch(skip, limit, sort, searchTerm);
    return results.map((user) => {
      const {
        _id: value,
        email: subtitle,
        firstname,
        lastname,
      } = user;

      const title = `${lastname || 'Unknown'}, ${firstname || 'Unknown'}`;

      return { value, title, subtitle };
    });
  }

  /**
   * After bound items are retrieved iterate through each one to set the sudoer
   * status of each bound item.
   * @todo Handle other compiled attributes such as LDAP Bind DN, Samba Service
   * Account, as other UserItemLists are replaced by the ItemTable.
   */
  processBoundItems(context, boundItems) {
    super.processBoundItems(context, boundItems);
    processSudoBoundItems(context, boundItems);
  }

  /**
   * Reset the MFA of one user
   * @param {String} id - The unique identifier of the user
   * @param {Number} exclusionUntil - The MFA exclusion expiration date
   * @param {Number} exclusionDays - The number of days for MFA exclusion after user is
   *   moved from staged to activated
   */
  resetMFA({ commit }, { exclusionUntil, exclusionDays, id }) {
    return SystemUserApi.resetMFA(id, exclusionUntil, exclusionDays)
      .then(() => {
        commit('UPDATE_ITEM', {
          id,
          attributes: {
            mfa: {
              exclusion: true,
              exclusionUntil,
              exclusionDays,
            },
          },
        });
      })
      .catch((error) => {
        if (error) {
          throw new Error(error);
        }
      });
  }

  /**
   * Step through the list of selected users and schedule the action for each user individually.
   * This ensures that the admin can modify the scheduled action at a later time for each user.
   *
   * If actions were scheduled in bulk, then the admin will not be able to change the schedule
   * for an individual user, but only for the group specified in the bulk action.
   *
   * @todo *Note:* The above was true when the functionality was originally written; it may not
   * be necessary to do each individually going forward
   *
   * @param {Array} ids - The unique identifier of the users to schedule
   */
  scheduleUserStateChange(context, { payload }) {
    const {
      action,
      emailSettings,
      eventDate,
      ids,
    } = payload || {};
    const { activate, suspend } = userStateActionType;
    const {
      sambaServiceUsers = [],
      suspendedUsers = [],
    } = context.getters.getUsersIneligibleForSuspension;
    const userCount = ids.length;
    let users;

    /**
     * For multiple users, step through the list of selected users and schedule
     *   the state change for each one that is eligible based on action type
     * For one user, assume they are eligible
     */
    if (userCount > 1) {
      let ineligibleUsers;

      // Determine the ineligible users by user state and unselect them
      if (action === activate) {
        ineligibleUsers = context.getters.getUsersIneligibleForActivation.activatedUsers || [];
      } else if (action === suspend) {
        ineligibleUsers = sambaServiceUsers.concat(suspendedUsers);
      }
      ineligibleUsers.forEach(id => context.dispatch('unselectItem', { id }));

      users = context.getters.getSelectedItems;
    } else {
      users = ids;
    }

    return new Promise((resolve) => {
      const { sendWelcomeEmail, welcomeEmailAddress } = emailSettings;

      const newState = action === suspend ? suspended : activated;
      const failedJobs = [];
      const scheduledUsers = [];
      const tasks = [];

      users.forEach((id) => {
        const eventToSchedule = {
          users: [id],
          state: newState,
          startDate: eventDate.toISOString(),
        };

        const item = context.getters.getItem(id);

        // Only send activation or welcome emails if the checkbox is checked and
        // the user is not SUSPENDED.
        if (action === activate) {
          const isSuspended = item?.state === suspended;
          if (!isSuspended) {
            eventToSchedule.send_activation_emails = sendWelcomeEmail;
          }
        }

        // Ony send activation_email_override if the sendWelcomeEmail flag is set and
        // the email address is different than the company or alternate email.
        if (userCount === 1
          && action === activate
          && sendWelcomeEmail
          && (welcomeEmailAddress !== item.email && welcomeEmailAddress !== item.alternateEmail)) {
          eventToSchedule.activation_email_override = welcomeEmailAddress;
        }

        tasks.push((cb) => {
          JobApi.scheduleStateChange(eventToSchedule)
            .then(() => {
              scheduledUsers.push(id);
              context.dispatch('ProgressModule/incrementCompleted', {}, { root: true });
              cb();
            })
            .catch(() => {
              failedJobs.push(id);
              context.dispatch('ProgressModule/incrementCompleted', {}, { root: true });
              cb();
            });
        });
      });

      if (tasks.length) {
        context.dispatch('ProgressModule/updateBulkActionStatus', true, { root: true });

        context.dispatch('ProgressModule/setCounter', {
          completed: 0,
          total: tasks.length,
        }, { root: true });

        parallelLimit(tasks, 5, () => {
          context.dispatch('ProgressModule/setCounter', {
            completed: tasks.length,
            total: tasks.length,
          }, { root: true });

          context.dispatch('ProgressModule/updateBulkActionStatus', false, { root: true });

          resolve({ failedJobs, scheduledUsers });
        });
      } else {
        resolve({ failedJobs: [], scheduledUsers: [] });
      }
    });
  }

  selectItems(context, { ids }) {
    context.commit('SELECT_ITEMS', { ids });
  }

  /**
   * Sync the `totalCount` and `totalCountOfAllItems` values for the collection
   *
   * @todo Ideally, this shouldn't be necessary as one would expect the fetch to
   * update this information. Research why this doesn't work correctly for some cases
   * @see https://jumpcloud.atlassian.net/browse/DE-2533
   */
  syncTotalCount({ commit }) {
    commit('SYNC_TOTAL_COUNT');
  }

  /**
   * Unlock a user
   * @param {String} id - The unique identifier of the user
   */
  unlock({ commit }, { id }) {
    return SystemUserApi.unlock(id)
      .then(() => {
        commit('UPDATE_ITEM', {
          id,
          attributes: {
            account_locked: false,
            badLoginAttempts: 0,
          },
        });
      })
      .catch((error) => {
        if (error) throw new Error(error);
      });
  }

  updateModifiedItemCount({ commit }) {
    commit('UPDATE_MODIFIED_ITEM_COUNT');
  }

  /**
   * Update the list of scheduled events for the provided list of users.
   *
   * @todo In the future, we will potentially want to fetch more than
   * one event per user (depth). For now we are only getting at most
   * one event (many users may not have events).
   */
  updateNextScheduledEvent({ commit }, ids) {
    ids.forEach((id) => {
      JobApi
        .getNextScheduledEvent(id)
        .then((nextEvent) => {
          const change = nextEvent.length ? cloneDeep(nextEvent) : {};
          commit('UPDATE_SCHEDULED_STATE_CHANGES', { change, id });
        })
        .catch(() => { /* NOOP */ });
    });
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  /**
   * Override method from base class in order to evaluate applied filters when
   * determining whether the SystemUsers collection can be refreshed.
   */
  canRefreshCollection(state = { features: { items: {} } }, getters = {}) {
    const { features } = state;
    const { boundItemsUrl, showBoundItems } = features;
    const { getAppliedFilters = [], getItemsToBind = [], getItemsToUnbind = {} } = getters;
    const { searchTerm } = features.items;
    const hasSelectionChanged = getItemsToBind.length > 0 || getItemsToUnbind.length > 0;

    let canRefresh = false;

    // Check to see if the collection is only being filtered by user state
    const filteredByState = getAppliedFilters.length === 1
      && getAppliedFilters[0].name === 'state';

    /**
     * If the only applied filter is for the user's state, allow the collection to refresh
     * as long as they're not searching or selecting anything. If we're not on an aside where
     * showBoundItems is set, also check to see if there are filters applied before refreshing.
     * In all other cases, don't override and just use the method from `CollectionModule.js`.
     */
    if (filteredByState) {
      canRefresh = !(searchTerm || hasSelectionChanged);
    } else if (!(showBoundItems && boundItemsUrl)) {
      canRefresh = !(searchTerm
        || getAppliedFilters.length
        || hasSelectionChanged);
    } else {
      canRefresh = super.canRefreshCollection(state, getters);
    }

    return canRefresh;
  }

  /**
   * Returns a list of staged and suspended users
   * @todo Look into removing/deprecating this method
   */
  getInactiveUsers(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);

    const stagedUsers = selectedItems.filter(item => item.state === staged);
    const suspendedUsers = selectedItems.filter(item => item.state === suspended);

    return { stagedUsers, suspendedUsers };
  }

  getStagedUsers(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);

    const stagedPendingPassword = selectedItems
      .filter(item => (item.state === staged) && !item.activated);

    const stagedWithPassword = selectedItems
      .filter(item => (item.state === staged) && item.activated);

    return { stagedPendingPassword, stagedWithPassword };
  }

  getSuspendedUsers(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);

    const suspendedUsers = selectedItems.filter(item => item.state === suspended);
    const suspendedWithPassword = suspendedUsers
      .filter(item => item.activated && !item.account_locked && !item.password_expired);

    return { suspendedUsers, suspendedWithPassword };
  }

  /**
   * Validates a list of selected users to determine whether any users are unable to be expired
   * @todo Are there any additional users that shouldn't be expired: pending, externally managed?
   * @todo We may want to update the language in the modal to explain why they aren't eligible?
   * @see https://jumpcloud.atlassian.net/browse/DE-1418
   */
  getUsersIneligibleForForcePasswordChange(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);
    return selectedItems.filter(item => item.password_expired || item.samba_service_user);
  }

  /**
   * Validates a list of selected users to determine whether any users
   * are unable to have MFA disabled
   */
  getUsersIneligibleForMFADisablement(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);
    return selectedItems.filter(item => !item.enable_user_portal_multifactor);
  }

  /**
   * Validates a list of selected users to determine whether any users
   * are unable to have MFA enabled
   */
  getUsersIneligibleForMFAEnablement(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);
    return {
      alreadyActiveUsers: selectedItems.filter(item => item.enable_user_portal_multifactor),
    };
  }

  /**
   * Validates a selected list of users that are prepared to have MFA reset.
   * Returns users that cannot have MFA reset
   */
  getUsersIneligibleForMFAReset(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);
    return selectedItems.filter((item) => {
      // don't allow users without MFA setup or required or in enrollment to reset MFA
      const mfaConfigured = get(item, 'mfa.configured', false);
      const isNotSetupAndNotRequired = !item.enable_user_portal_multifactor && !mfaConfigured;
      const inEnrollment = get(item, 'mfa.exclusion', false);
      return isNotSetupAndNotRequired && !inEnrollment;
    });
  }

  /**
   * Validates a list of selected users to ensure that we do not attempt to restore users
   * who are not currently suspended. We group the activated users together so that we can
   * remove them from the selected items if the user confirms the action.
   */
  getUsersIneligibleForActivation(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);

    const activatedUsers = selectedItems.filter(item => item.state === activated);

    return { activatedUsers };
  }

  /**
   * Validates a list of selected users to ensure that we do not attempt to suspend externally
   * managed users. We group the unsuspendables together so that we can remove them
   * from the selected items if the user confirms the action.
   */
  getUsersIneligibleForSuspension(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);
    // Check if the Samba service user is part of the selection. These users cannot be suspended.
    const sambaServiceUsers = selectedItems.filter(item => item.samba_service_user);
    const suspendedUsers = selectedItems.filter(item => item.state === suspended);

    return {
      sambaServiceUsers,
      suspendedUsers,
    };
  }

  /**
   * Validates a list of selected users to determine whether any users are unable to be unlocked
   */
  getUsersIneligibleForUnlock(state, getters) {
    const selectedItems = getSelectedItemAttributes(state, getters);
    return selectedItems.filter(item => !item.account_locked);
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  RESET_COLLECTION(state, payload) {
    super.RESET_COLLECTION(state, payload);

    if (!payload || (payload && !payload.soft)) {
      const initialState = this.getInitialState();
      resetSudoState(state, initialState);
      BindStatusMixin.resetBindStatusState(state, initialState);
    }
  }

  /**
   * Set the list of scheduled state changes for the list of users.
   * Use union to include additional changes when scrolling the users list.
   *
   * Use uniqBy to protect against adding duplicate entries for a user when fetchCollection
   * is called multiple times for the same list of users (e.g. during load it gets called
   * multiple times).
   */
  SET_SCHEDULED_STATE_CHANGES(state, changes) {
    // scheduledStateChanges is an Observer object; clone it so that we're just comparing
    // arrays of objects
    const current = cloneDeep(state.scheduledStateChanges);
    const updates = uniqBy(union(current, changes), 'scheduledJobId');

    Vue.set(state, 'scheduledStateChanges', updates);
  }

  /**
   * If the totalCount and totalCountOfAllItems are different because of user state
   * filtering on the main users page, update the values to be equal so that the
   * item action bar selection text is correct.
   */
  SYNC_TOTAL_COUNT(state) {
    const { items } = state.entities;

    if (items.totalCountOfAllItems !== items.totalCount) {
      Vue.set(state.entities.items, 'totalCountOfAllItems', items.totalCount);
    }
  }

  UPDATE_MODIFIED_ITEM_COUNT(state) {
    state.features.modifiedItemCount += 1; // eslint-disable-line no-param-reassign
  }

  /**
   * Add, replace or remove scheduled changes for an individual user.
   */
  UPDATE_SCHEDULED_STATE_CHANGES(state, { change, id }) {
    // Get the list of changes excluding this user
    const filteredEvents = state.scheduledStateChanges.filter(event => event.systemUserId !== id);

    if (isEmpty(change)) {
      // Delete any state changes for the user by filtering out existing changes
      // and updating the list with the result
      Vue.set(state, 'scheduledStateChanges', filteredEvents);
    } else {
      // Add changes for this user to the filtered list
      Vue.set(state, 'scheduledStateChanges', [
        ...filteredEvents,
        ...change,
      ]);
    }
  }
}

export default new SystemUserCollectionClass(SystemUserApi, '_id', 'lastname').extractModule();
